import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import {
	type PrefetchedConsentPreferences,
	shouldRenderBannerPrefetched,
} from '@atlassian/browser-storage-controls';

import { ssrBannerManager } from '@confluence/ssr-utilities/entry-points/SSRBannerManager';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs';
import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer';

import { MIN_BANNER_HEIGHT, BANNER_NAME } from './constants';
import { expVal } from '@confluence/feature-experiments';

declare global {
	interface Window {
		__COOKIE_CONSENT_PREFERENCES__?: PrefetchedConsentPreferences | null;
	}
}

export function useCookiesConsentBanner(
	bannerState?: BannerStateContainer | undefined,
	bannerRef?: React.RefObject<HTMLDivElement | null>,
) {
	const cookiesDialogRef = useRef<HTMLDivElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [height, setHeight] = useState(MIN_BANNER_HEIGHT);
	const prefetchedData = Object.keys(window?.__COOKIE_CONSENT_PREFERENCES__ || {}).length
		? window.__COOKIE_CONSENT_PREFERENCES__
		: undefined;
	const { cookiesShowBannerToken } = getSSRRenderInputs();

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	// Use useMemo to calculate shouldRenderInitial only once, regardless of SSR or client
	const shouldRenderInitial = useMemo(() => {
		// TODO fix hacky ternary because of type issue
		// If there is no data, it may be because there is not a userId. Defer to package internal logic to determine whether to render
		const initialValue = prefetchedData
			? shouldRenderBannerPrefetched(prefetchedData)
			: shouldRenderBannerPrefetched();

		if (
			cookiesShowBannerToken !== undefined &&
			expVal('confluence_frontend_cookies_show_banner_ssr', 'isEnabled', false)
		) {
			/**
			 * Use cookiesShowBannerToken cookie directly instead of relying on prefetched data logic.
			 * This avoids SSR hydration mismatches where the server shows the banner based on
			 * prefetched data, but the client-side ConsentBanner component dismisses it because
			 * the fallback cookie isn't properly set during SSR prefetching.
			 *
			 * This is because the cookie consent logic does not set the 'fallback' cookie when we prefetch the data.
			 * Because of this, shouldRenderBannerPrefetched will always end up returning true if we prefetch.
			 *
			 * If we look at the getShowBanner code in platform/packages/policy-platform/browser-storage-controls/src/ui/consent-banner/use-banner-visibility/index.ts
			 * we can see that it will use this show banner cookie to determine if the banner should be shown
			 * as long as we aren't in localConsentMode (which we aren't since we do not pass localConsentMode=true to ConsentBanner
			 * in confluence/next/packages/cookies-consent-banner/src/CookiesConsentBanner.tsx)
			 *
			 * The cookiesShowBannerToken ('atl-bsc-show-banner') is the source for banner
			 * visibility and is consistently available in both SSR and client contexts.
			 */
			const shouldShowBanner =
				cookiesShowBannerToken !== undefined && cookiesShowBannerToken !== null
					? !!Number(cookiesShowBannerToken)
					: null;
			if (isSSR && shouldShowBanner) {
				ssrBannerManager.registerBanner({
					name: BANNER_NAME,
					height: MIN_BANNER_HEIGHT,
				});
			}
			return shouldShowBanner;
		}

		// Only register banner during SSR
		if (isSSR && initialValue) {
			ssrBannerManager.registerBanner({
				name: BANNER_NAME,
				height: MIN_BANNER_HEIGHT,
			});
		}

		return initialValue;
	}, [prefetchedData, isSSR, cookiesShowBannerToken]);

	const [shouldRender, setShouldRender] = useState<boolean | null>(
		isSSR ? shouldRenderInitial : null,
	);

	if (shouldRender) {
		bannerState?.show(BANNER_NAME, height);
	} else if (shouldRender === false) {
		bannerState?.hide(BANNER_NAME);
	}

	const findCookiesDialog = useCallback(() => {
		if (cookiesDialogRef?.current) {
			return cookiesDialogRef?.current;
		}

		if (!bannerRef?.current) {
			return null;
		}
		const childDivs = bannerRef?.current.getElementsByTagName('div');
		let cookiesDialog = null;
		for (const element of Object.values(childDivs)) {
			if (!element.offsetHeight) {
				continue;
			}
			// @ts-ignore - Type 'HTMLDivElement' is not assignable to type 'null'
			// Using `ts-ignore` and not `ts-expect-error` here as TypeScript will fail on this line without `ts-ignore` but with `ts-expect-error`
			// This error was introduced after upgrading to TypeScript 5
			cookiesDialog = element;
			break;
		}
		cookiesDialogRef.current = cookiesDialog;
		return cookiesDialog;
	}, [bannerRef]);

	useEffect(() => {
		if (shouldRender || shouldRenderInitial) {
			addUFOCustomData({
				isCookieConsentBannerDisplayed: true,
				...(prefetchedData && {
					prefetchedConsentPreferences: JSON.stringify(prefetchedData),
				}),
			});
		}
	}, [shouldRender, shouldRenderInitial, prefetchedData]);

	useEffect(() => {
		if (shouldRender) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				action: 'rendered',
				actionSubject: 'cookiesConsentBanner',
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldRender]);

	const onWidthChange = useCallback(() => {
		const cookiesDialog = findCookiesDialog();
		if (!cookiesDialog) {
			return;
		}
		addUFOCustomData({
			cookieBannerHeight: cookiesDialog.offsetHeight,
		});
		setHeight(cookiesDialog.offsetHeight);
	}, [findCookiesDialog]);

	const showBanner = useCallback(() => {
		setShouldRender(true);
		onWidthChange();
	}, [onWidthChange]);

	const hideBanner = useCallback(() => {
		setShouldRender(false);
	}, []);

	return {
		shouldRender,
		showBanner,
		hideBanner,
		onWidthChange,
		prefetchedData,
	};
}
